import { useState, useEffect } from "react";
import { Phone, Send, CheckCircle, Shield, Award, Star, ThumbsUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
      ))}
    </div>
  );
}

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

export default function DevisPAC() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("PAC Air/Eau");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(res => {
      setReviews(res.data.reviews);
      setRating(res.data.rating);
      setTotal(res.data.total);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Devis Pompe à Chaleur Gratuit | Alter Energies - Lot-et-Garonne (47)";
    const meta = document.querySelector('meta[name="description"]');
    const content = "Demandez votre devis gratuit pour l'installation d'une pompe à chaleur en Lot-et-Garonne. Artisan certifié RGE QualiPAC, reste à charge dès 950€. Rappel sous 24h.";
    if (meta) { meta.setAttribute("content", content); } else { const m = document.createElement("meta"); m.name = "description"; m.content = content; document.head.appendChild(m); }
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) { robots.setAttribute("content", "noindex, nofollow"); } else { const r = document.createElement("meta"); r.name = "robots"; r.content = "noindex, nofollow"; document.head.appendChild(r); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !phone.trim()) { setError("Veuillez remplir votre nom et téléphone."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), phone: phone.trim(), service, message: message.trim() });
      setSuccess(true);
      setName(""); setPhone(""); setService("PAC Air/Eau"); setMessage("");
      if (window.gtag) {
        window.gtag('event', 'conversion', { 'send_to': 'AW-17966418854/7_mxCKWqoZwcEKaXh_dC' });
      }
    } catch { setError("Une erreur est survenue. Appelez-nous au 06 28 51 49 15."); }
    finally { setLoading(false); }
  };

  if (success) return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-[#6AB023]" /></div>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-3">Demande envoyée !</h1>
        <p className="text-gray-600 text-lg mb-2">François vous rappelle sous 24h.</p>
        <p className="text-gray-500 text-sm">Pour toute urgence : <a href="tel:0628514915" className="text-[#6AB023] font-bold">06 28 51 49 15</a></p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">

      {/* Header minimaliste */}
      <header className="bg-white border-b border-gray-100 py-4">
        <div className="container mx-auto px-4 max-w-5xl flex items-center justify-between">
          <a href="/">
            <img src="https://customer-assets.emergentagent.com/job_chauffage-pac/artifacts/kjcz0m09_ChatGPT%20Image%2011%20fe%CC%81vr.%202026%20a%CC%80%2019_18_47%20%281%29.png" alt="Alter Energies" className="h-10 w-auto" />
          </a>
          <a href="tel:0628514915">
            <Button className="bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-6 h-11 gap-2 text-sm">
              <Phone className="w-4 h-4" />06 28 51 49 15
            </Button>
          </a>
        </div>
      </header>

      {/* Hero + Formulaire */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-10 items-start">

            {/* Colonne gauche — Argumentaire */}
            <div className="pt-4">
              <div className="inline-flex items-center gap-2 bg-green-100 text-[#6AB023] rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />Certifié RGE QualiPAC
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
                Pompe à chaleur en Lot-et-Garonne<br /><span className="text-[#6AB023]">dès 950€ reste à charge</span>
              </h1>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                François Delattre installe votre PAC air/eau ou air/air. Aides MaPrimeRénov' + CEE déduites, dossiers gérés. Rappel sous 24h.
              </p>

              {/* Chiffrage rapide */}
              <div className="bg-white border-2 border-[#6AB023]/20 rounded-xl p-5 mb-8">
                <p className="font-bold text-gray-900 mb-3 text-sm">Exemple : maison 100m²</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">PAC Air/Eau installée</span><span className="font-semibold">12 500€</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">MaPrimeRénov'</span><span className="font-semibold text-[#6AB023]">- 5 000€</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Prime CEE</span><span className="font-semibold text-[#6AB023]">- 6 550€</span></div>
                  <div className="border-t pt-2 flex justify-between"><span className="font-bold">Reste à charge</span><span className="font-extrabold text-[#6AB023] text-lg">950€*</span></div>
                </div>
                <p className="text-xs text-gray-400 mt-2">*Selon revenus du foyer</p>
              </div>

              {/* Trust badges */}
              <div className="space-y-3">
                {[
                  { icon: Shield, text: "Certifié RGE QualiPAC — accès à toutes les aides" },
                  { icon: Award, text: "500+ installations en Lot-et-Garonne depuis 20 ans" },
                  { icon: ThumbsUp, text: `${rating.toFixed(1)}/5 sur Google — ${total} avis clients` },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0"><item.icon className="w-4 h-4 text-[#6AB023]" /></div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            {/* Colonne droite — Formulaire */}
            <div>
              <Card className="border border-gray-200 rounded-xl shadow-xl">
                <CardContent className="p-6 md:p-8">
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Votre devis gratuit en 24h</h2>
                    <p className="text-sm text-gray-500">Sans engagement · François vous rappelle</p>
                  </div>

                  {total > 0 && (
                    <div className="bg-green-50/60 rounded-lg p-3 flex items-center justify-center gap-2 mb-6">
                      <GoogleLogo />
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
                        ))}
                      </div>
                      <span className="font-semibold text-gray-900 text-sm">{rating.toFixed(1)}</span>
                      <span className="text-gray-400 text-sm">({total} avis)</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre nom <span className="text-red-400">*</span></label>
                      <Input type="text" placeholder="Jean Dupont" value={name} onChange={(e) => setName(e.target.value)} className="h-12 border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre téléphone <span className="text-red-400">*</span></label>
                      <Input type="tel" placeholder="06 12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Type de projet</label>
                      <select value={service} onChange={(e) => setService(e.target.value)} className="w-full h-12 border border-gray-300 rounded-lg px-3 text-sm text-gray-700 bg-white focus:outline-none">
                        <option value="PAC Air/Eau">Pompe à chaleur Air/Eau</option>
                        <option value="PAC Air/Air">Pompe à chaleur Air/Air</option>
                        <option value="Remplacement chaudière fioul">Remplacement chaudière fioul</option>
                        <option value="Chauffe-eau thermodynamique">Chauffe-eau thermodynamique</option>
                        <option value="Autre">Autre / Je ne sais pas</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre commune <span className="text-xs text-gray-400 font-normal">(optionnel)</span></label>
                      <Input type="text" placeholder="Ex: Villeneuve-sur-Lot" value={message} onChange={(e) => setMessage(e.target.value)} className="h-12 border-gray-300 rounded-lg" />
                    </div>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    <Button type="submit" disabled={loading} className="w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-13 text-base gap-2">
                      {loading ? <span className="flex items-center gap-2"><svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Envoi...</span> : <><Send className="w-5 h-5" />Recevoir mon devis gratuit</>}
                    </Button>
                    <p className="text-xs text-gray-400 text-center">Vos données restent confidentielles</p>
                  </form>
                </CardContent>
              </Card>

              {/* Appel direct */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500 mb-2">Ou appelez directement :</p>
                <a href="tel:0628514915">
                  <Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 font-bold rounded-full px-8 h-11 gap-2">
                    <Phone className="w-4 h-4" />06 28 51 49 15
                  </Button>
                </a>
              </div>
              <div className="mt-3 text-center">
  <a href="https://maps.app.goo.gl/C8s7vSJPZ2sHXxe16" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#6AB023] transition-colors">
    <GoogleLogo />
    <span>Voir notre fiche Google</span>
  </a>
</div>
            </div>
          </div>
        </div>
      </section>

      {/* Avis Google — social proof rapide */}
      {reviews.length > 0 && (
        <section className="py-12 bg-[#F9FAFB]">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3"><GoogleLogo /><span className="text-sm font-medium text-gray-500">Avis Google vérifiés</span></div>
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl font-bold text-gray-900">{rating.toFixed(1)}</span>
                <div><StarRating rating={Math.round(rating)} /><p className="text-xs text-gray-500 mt-0.5">{total} avis</p></div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {reviews.slice(0, 3).map((review) => (
                <Card key={review.name} className="bg-white border border-gray-100 rounded-xl shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-[#6AB023] rounded-full flex items-center justify-center text-white font-bold text-xs">{getInitials(review.name)}</div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
                        <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => (<Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />))}</div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{review.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer minimaliste */}
      <footer className="py-6 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-5xl text-center text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Alter Energies — François Delattre · Chauffagiste RGE QualiPAC · Pinel-Hauterive (47)</p>
        </div>
      </footer>

    </div>
  );
}
